import React, { createContext, useContext, useEffect, useState, useRef } from 'react';
import { Session, User } from '@supabase/supabase-js';
import { supabase } from '../../lib/supabase';
import { signInWithGoogle as signInWithGoogleHelper } from '../../lib/authProviders';

interface AuthContextType {
    session: Session | null;
    user: User | null;
    userId: string | null;
    loading: boolean;
    authReady: boolean; // True only after initial getSession() resolves AND onAuthStateChange initial event is processed
    signOut: () => Promise<void>;
    signInWithPassword: (email: string, password: string) => Promise<any>;
    signUp: (email: string, password: string, firstName?: string, lastName?: string) => Promise<any>;
    signInWithGoogle: () => Promise<any>;
}

const AuthContext = createContext<AuthContextType>({
    session: null,
    user: null,
    userId: null,
    loading: true,
    authReady: false,
    signOut: async () => { },
    signInWithPassword: async () => { },
    signUp: async () => { },
    signInWithGoogle: async () => { },
});

export const useAuth = () => useContext(AuthContext);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const [session, setSession] = useState<Session | null>(null);
    const [user, setUser] = useState<User | null>(null);
    const [loading, setLoading] = useState(true);
    const [authReady, setAuthReady] = useState(false);
    
    // Use ref to track subscription state - ensures SINGLETON subscription
    const subscriptionRef = useRef<{ unsubscribe: () => void } | null>(null);
    const initialEventProcessedRef = useRef(false);

    useEffect(() => {
        let isMounted = true;

        // GUARANTEE: Subscribe exactly once - never resubscribe
        if (subscriptionRef.current) {
            if (import.meta.env.DEV) console.log('[AuthProvider] Subscription already exists, skipping');
            return;
        }

        // 1. Get initial session FIRST before setting up listener
        if (import.meta.env.DEV) console.log('[AuthProvider] Getting initial session...');
        supabase.auth.getSession().then(({ data: { session } }) => {
            if (!isMounted) return;
            
            if (import.meta.env.DEV) {
                console.log('[AuthProvider] Initial session check complete:', {
                    hasSession: !!session,
                    userId: session?.user?.id
                });
            }
            
            setSession(session);
            setUser(session?.user ?? null);
            // Don't set authReady here - wait for initial event
        }).catch((err) => {
            if (import.meta.env.DEV) {
                console.error('[AuthProvider] getSession error:', {
                    message: err.message
                });
            }
            if (!isMounted) return;
        });

        // 2. Listen for changes - set authReady only after initial event is processed
        const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
            if (!isMounted) return;
            
            if (import.meta.env.DEV) {
                console.log('[auth]', {
                    authReady,
                    hasSession: !!session,
                    userId: session?.user?.id,
                    event
                });
            }
            
            setSession(session);
            setUser(session?.user ?? null);
            setLoading(false);
            
            // Mark initial event as processed and set authReady (only once, never flip back to false)
            if (!initialEventProcessedRef.current) {
                initialEventProcessedRef.current = true;
                setAuthReady(true);
                if (import.meta.env.DEV) console.log('[AuthProvider] Auth ready (initial event processed)');
            }
        });

        // Store subscription ref - ensures we never resubscribe
        subscriptionRef.current = subscription;

        return () => {
            isMounted = false;
            if (subscriptionRef.current) {
                subscriptionRef.current.unsubscribe();
                subscriptionRef.current = null;
            }
        };
    }, []); // EMPTY DEPS - ensures SINGLETON subscription

    const signOut = async () => {
        await supabase.auth.signOut();
    };

    const signInWithPassword = async (email: string, password: string) => {
        if (import.meta.env.DEV) {
            console.log('[AUTH] Using email + password login');
        }
        return supabase.auth.signInWithPassword({
            email,
            password
        });
    };

    const signUp = async (email: string, password: string, firstName?: string, lastName?: string) => {
        if (import.meta.env.DEV) {
            console.log('[AUTH] Using email + password signup');
        }
        return supabase.auth.signUp({
            email,
            password,
            options: {
                data: {
                    first_name: firstName || '',
                    last_name: lastName || '',
                    full_name: firstName && lastName ? `${firstName} ${lastName}` : ''
                }
            }
        });
    };

    const signInWithGoogle = async () => {
        if (import.meta.env.DEV) {
            console.log('[AUTH] Using Google OAuth login');
        }
        return signInWithGoogleHelper();
    };

    return (
        <AuthContext.Provider value={{ 
            session, 
            user, 
            userId: user?.id || null,
            loading, 
            authReady, 
            signOut, 
            signInWithPassword, 
            signUp,
            signInWithGoogle 
        }}>
            {children}
        </AuthContext.Provider>
    );
};
